<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { QuizterViewer, useQuizterLoader, useQuizterStore } from '@ilteducation/games';
import type { SelectedVideo } from '@ilteducation/games';
import { CloseEndedQuestion } from '@ilteducation/exercise-service-types';
import { useLocale, useLocalisation } from '@/plugins/translation';

// Load Quizter translations from the Quizter Lokalise project
const { includeAdditionalProject } = useLocalisation();
includeAdditionalProject('Quizter', false);

const { t } = useLocale();
const route = useRoute();
const router = useRouter();
const quizterStore = useQuizterStore();
const { loadSharedQuizter } = useQuizterLoader();

const shareId = computed(() => route.params.shareId as string);

const gamesPath = router.resolve({ name: 'Games' }).href;
const absoluteGamesUrl = `${window.location.origin}${gamesPath}`;

const returnParams = computed(() => ({
  url: window.location.href,
  text: t('quiz.back'),
}));

const returnParamsPlayer = computed(() => ({
  url: absoluteGamesUrl,
  text: t('quizter.back_to_games'),
}));

// Resolve the shared quizter into the viewer's video list. `activityId` comes from the response
// body (never the URL) and is what we play/host with.
const videos = ref<SelectedVideo[]>([]);
const activityId = ref<string | null>(null);
const loading = ref(false);
const loadError = ref(false);

const isSubscribed = ref(false);
const subscribing = ref(false);
const subscribeMessage = ref<string | null>(null);
const subscribeError = ref(false);

const startError = ref(false);

onMounted(async () => {
  loading.value = true;
  try {
    const [quizter, mySubscribedQuizters] = await Promise.all([
      loadSharedQuizter(shareId.value),
      quizterStore.fetchMySubscribedQuizters().catch(() => []),
    ]);
    videos.value = quizter.videos;
    activityId.value = quizter.activityId;
    isSubscribed.value = mySubscribedQuizters.some((q) => q.shareId === shareId.value);
  } catch {
    loadError.value = true;
  } finally {
    loading.value = false;
  }
});

const goBack = () => {
  router.push({ name: 'QuizterHomePage' });
};

const handleToggleSubscribe = async () => {
  if (subscribing.value) {
    return;
  }
  const wasSubscribed = isSubscribed.value;
  subscribing.value = true;
  try {
    if (wasSubscribed) {
      await quizterStore.unsubscribeSharedQuizter(shareId.value);
    } else {
      await quizterStore.subscribeSharedQuizter(shareId.value);
    }
    isSubscribed.value = !wasSubscribed;
    subscribeError.value = false;
    subscribeMessage.value = wasSubscribed ? t('quizter.unsubscribe_success') : t('quizter.subscribe_success');
  } catch {
    subscribeError.value = true;
    subscribeMessage.value = t('quizter.subscribe_error');
  } finally {
    subscribing.value = false;
  }
};

const handleStart = async (resourceIds: string[], questions?: CloseEndedQuestion[]) => {
  if (resourceIds.length === 0 || !activityId.value) {
    return;
  }
  startError.value = false;
  try {
    await quizterStore.createGame(
      resourceIds,
      false,
      activityId.value,
      questions,
      returnParams.value,
      returnParamsPlayer.value,
    );
    const gameId = quizterStore.gameId;
    if (gameId) {
      router.push({ name: 'HostQuizter', params: { gameId } });
    } else {
      startError.value = true;
    }
  } catch {
    startError.value = true;
  }
};
</script>

<template>
  <QuizterViewer
    :videos="videos"
    :loading="loading"
    :start="handleStart"
    :can-subscribe="true"
    :is-subscribed="isSubscribed"
    :subscribing="subscribing"
    @toggle-subscribe="handleToggleSubscribe"
    @back="goBack"
  />
  <v-snackbar v-model="loadError" color="error" location="bottom">
    {{ t('quizter.generic_error') }}
  </v-snackbar>
  <v-snackbar v-model="startError" color="error" location="bottom">
    {{ t('quizter.generic_error') }}
  </v-snackbar>
  <v-snackbar
    :model-value="!!subscribeMessage"
    :color="subscribeError ? 'error' : 'success'"
    location="bottom"
    @update:model-value="subscribeMessage = null"
  >
    {{ subscribeMessage }}
  </v-snackbar>
</template>

<style scoped lang="scss"></style>
